import {
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { EmphasizedNumber, Icons, ItemImageArea, SkeletonText } from 'utoniq-ui'

import { ItemSelectBoxFragment } from '@/clients/utoniq-core/schema'
import { ItemIcon } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

export type ItemSelectBoxProps = {
  checked?: boolean
  item: ItemSelectBoxFragment
  onSelect?: (itemId: string) => void
  showIcon?: boolean
}

export const ItemSelectBox: Organism<ItemSelectBoxProps> = ({
  checked,
  item,
  onSelect,
  showIcon,
}) => {
  const { t } = useTranslation('consoleCollectionCreate')

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: checked ? 'primary.main' : 'grey.50',
        position: 'relative',
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={4}
          alignItems="center"
          sx={{ position: 'relative' }}
        >
          {showIcon && (
            <Stack
              sx={{
                zIndex: 1,
                marginInline: 'auto',
                pl: 1,
                width: 104,
                position: 'absolute',
                top: 8,
                left: 16,
              }}
            >
              <ItemIcon type={item.type} />
            </Stack>
          )}
          <ItemImageArea
            size="small"
            src={item.thumbnail.front?.smallUri ?? ''}
          />
          <Stack flex={1} spacing={1}>
            <Typography variant="h6" sx={styleSets.lineCamp(2)}>
              {item.title}
            </Typography>
            <Stack direction="row">
              <Stack flex={1} spacing={1}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t('issuedNumber', { number: item.issuedNumber ?? '∞' })}
                </Typography>
                <EmphasizedNumber variant="h5" color="text.secondary">
                  {t('price', { number: item.price ?? 0 })}
                </EmphasizedNumber>
              </Stack>
              {item.isPremium && (
                <Stack justifyContent="flex-end">
                  <Icons.MembersOnly sx={{ color: 'info.main' }} />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
      <CardActionArea
        onClick={() => {
          if (!onSelect || checked) {
            return
          }
          return onSelect(item.id)
        }}
        sx={{
          position: 'absolute',
          zIndex: 2,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />
    </Card>
  )
}

export const ItemSelectBoxSkeleton: Organism = () => (
  <Card elevation={0} sx={{ borderRadius: 2 }}>
    <CardContent>
      <Stack direction="row" spacing={4} alignItems="center">
        <Skeleton variant="rectangular" width={60} height={96} />
        <Stack flex={1} alignSelf="flex-start">
          <SkeletonText variant="medium" width="80%" />
          <SkeletonText variant="medium" width="80%" />
          <Stack sx={{ pt: 1 }}>
            <SkeletonText variant="small" width="40%" />
            <SkeletonText variant="small" width="20%" />
          </Stack>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
)
